import { Box, Button, Typography } from '@mui/material';
import {
  ArrowCounterClockwise,
  ArrowLeft,
  BoundingBox,
  Clock,
  Plus,
} from '@phosphor-icons/react';
import { useState } from 'react';
import RestoreConfirmModal from './RestoreConfirmModal';

export interface VersionItem {
  id: string;
  name: string;
  owner: string;
  lastModified: string;
  isLive?: boolean;
  publishedInfo?: string;
}

export type VersionControlProps = {
  selectedVersionId: string;
  onSelectVersion: (id: string) => void;
  onShowHistory: (id: string) => void;
  onHideHistory: () => void;
  onNavigateToProduction: () => void;
  onAddNew: () => void;
  onPublish: () => void;
  onGoToEditing?: () => void;
  liveVersion: VersionItem;
  versions: VersionItem[];
  historyEntries: VersionItem[];
  historyVersionName: string;
  onRestore?: (id: string) => void;
  showHistory?: boolean;
};

interface VersionControlDropdownProps {
  isDarkTheme: boolean;
  selectedVersionId: string;
  onSelectVersion: (id: string) => void;
  onShowHistory: (id: string) => void;
  onHideHistory: () => void;
  onNavigateToProduction: () => void;
  onAddNew: () => void;
  onPublish: () => void;
  liveVersion: VersionItem;
  versions: VersionItem[];
  historyEntries: VersionItem[];
  historyVersionName: string;
  onRestore?: (id: string) => void;
  showHistory?: boolean;
}

const VersionControlDropdown = ({
  isDarkTheme,
  selectedVersionId,
  onSelectVersion,
  onShowHistory,
  onHideHistory,
  onNavigateToProduction,
  onAddNew,
  onPublish,
  liveVersion,
  versions,
  historyEntries,
  historyVersionName,
  onRestore,
  showHistory = false,
}: VersionControlDropdownProps) => {
  const bg = isDarkTheme ? '#101828' : '#FFFFFF';
  const textPrimary = isDarkTheme ? '#D0D5DD' : '#344054';
  const textSecondary = isDarkTheme ? '#98A2B3' : '#667085';
  const sectionLabel = isDarkTheme ? '#98A2B3' : '#667085';
  const hoverBg = isDarkTheme ? '#1D2939' : '#F9FAFB';
  const selectedBg = isDarkTheme ? '#1D2939' : '#EFF4FF';
  const borderSelected = '#546BEA';
  const borderColor = isDarkTheme ? '#344054' : '#E4E7EC';

  const [restoreTarget, setRestoreTarget] = useState<VersionItem | null>(null);

  const currentEntry = historyEntries.find(v => v.isLive);

  if (showHistory) {
    return (
      <Box
        sx={{
          width: 400,
          minWidth: 320,
          maxWidth: 400,
          p: 2.5,
          background: bg,
          borderRadius: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            onClick={onHideHistory}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <ArrowLeft size={20} color={textPrimary} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              my: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              <Clock size={20} color="#546BEA" />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '16px',
                  color: textPrimary,
                  mb: 1,
                  mt: 0.125,
                }}
              >
                {historyVersionName} History
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: textSecondary,
                }}
              >
                Select the specific version of your variant that you would like to restore.
              </Typography>
            </Box>
          </Box>
        </Box>
        {historyEntries.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 5,
              gap: 1.5,
            }}
          >
            <Clock size={32} color={textSecondary} />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '15px',
                color: textPrimary,
              }}
            >
              No history yet
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                color: textSecondary,
                textAlign: 'center',
              }}
            >
              Publish or make changes to create history entries.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 400, overflowY: 'auto' }}>
            {historyEntries.map(v => {
              const isCurrent = v.isLive;
              return (
                <Box
                  key={v.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.25,
                    px: 1.5,
                    borderRadius: '8px',
                    bgcolor: bg,
                    border: `1px solid ${borderColor}`,
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {v.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: textSecondary,
                        mt: 0.25,
                      }}
                    >
                      Owner: {v.owner} · Last modified: {v.lastModified}
                    </Typography>
                  </Box>
                  {isCurrent ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: '999px',
                        bgcolor: isDarkTheme ? 'rgba(84, 107, 234, 0.12)' : '#EFF4FF',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          color: '#546BEA',
                        }}
                      >
                        Current
                      </Typography>
                    </Box>
                  ) : (
                    <Button
                      onClick={() => setRestoreTarget(v)}
                      startIcon={<ArrowCounterClockwise size={20} color="#546BEA" />}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#546BEA',
                        textTransform: 'none',
                        px: 1,
                        minWidth: 'auto',
                        gap: 0.25,
                      }}
                    >
                      Restore
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        <RestoreConfirmModal
          show={!!restoreTarget}
          onHide={() => setRestoreTarget(null)}
          onConfirm={() => {
            if (restoreTarget) {
              if (onRestore) {
                onRestore(restoreTarget.id);
              } else {
                onSelectVersion(restoreTarget.id);
              }
              setRestoreTarget(null);
            }
          }}
          currentVersionName={currentEntry?.name || historyVersionName}
          restoreVersionName={restoreTarget?.name || ''}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 400,
        minWidth: 320,
        maxWidth: 400,
        p: 2.5,
        background: bg,
        borderRadius: '12px',
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: '16px',
          color: textPrimary,
          mb: 1,
        }}
      >
        Versions
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          color: textSecondary,
          mb: 2,
        }}
      >
        Work on multiple versions of your agent, publish when ready.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1,
            mx: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: '#12B76A',
            }}
          />
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: sectionLabel,
              letterSpacing: '0.5px',
            }}
          >
            CURRENTLY LIVE
          </Typography>
        </Box>
        <Box
          onClick={onNavigateToProduction}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.25,
            px: 1.5,
            borderRadius: '8px',
            bgcolor: hoverBg,
            border: `1px solid ${borderColor}`,
            cursor: 'pointer',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontWeight: 500, fontSize: '14px', color: textPrimary }}
            >
              {liveVersion.name}
            </Typography>
            <Typography
              sx={{ fontSize: '12px', color: textSecondary, mt: 0.25 }}
            >
              {liveVersion.publishedInfo}
            </Typography>
          </Box>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onShowHistory(liveVersion.id);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.5,
              borderRadius: '4px',
              cursor: 'pointer',
              '&:hover': { bgcolor: isDarkTheme ? '#344054' : '#E4E7EC' },
            }}
          >
            <Clock size={18} color={textSecondary} />
          </Box>
        </Box>
      </Box>

      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            mt: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mx: 1,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: '#12B76A',
              }}
            />
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 400,
                color: sectionLabel,
                letterSpacing: '0.5px',
              }}
            >
              YOUR VERSIONS
            </Typography>
          </Box>
          <Button
            onClick={onAddNew}
            endIcon={<Plus size={16} />}
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: '#546BEA',
              textTransform: 'none',
              border: '1px solid #546BEA',
              borderRadius: '5px',
              px: 1.75,
              '&:hover': { bgcolor: hoverBg },
            }}
          >
            Add New
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {versions.map((v) => {
            const isSelected = v.id === selectedVersionId;
            return (
              <Box
                key={v.id}
                onClick={() => onSelectVersion(v.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.25,
                  px: 1.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: isSelected ? selectedBg : 'transparent',
                  border: isSelected ? `1px solid ${borderSelected}` : '1px solid transparent',
                  '&:hover': { bgcolor: isSelected ? selectedBg : hoverBg },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    <BoundingBox
                      size={15}
                      color={isSelected ? '#3650DE' : '#000000'}
                      weight={isSelected ? 'fill' : 'regular'}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: isSelected ? '#3650DE' : textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {v.name}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: textSecondary,
                      mt: 0.25,
                    }}
                  >
                    Owner: {v.owner} · Last modified: {v.lastModified}
                  </Typography>
                </Box>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowHistory(v.id);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isDarkTheme ? '#344054' : '#E4E7EC' },
                  }}
                >
                  <Clock size={18} color={isSelected ? borderSelected : textSecondary} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default VersionControlDropdown;
